import { Options, RRule } from 'rrule';
import DateF from './date-f';

const weekdayMap = {
  Sunday: RRule.SU,
  Monday: RRule.MO,
  Tuesday: RRule.TU,
  Wednesday: RRule.WE,
  Thursday: RRule.TH,
  Friday: RRule.FR,
  Saturday: RRule.SA,
};

const weeks = [1, 2, 3, 4];

/**
 * returns the bysetpos and bymonthday for rrulejs offset properties
 * where months do not have a day for the required rule
 */
function getByMonthDay(date: string) {
  const dayOfMonth = new Date(date).getUTCDate();
  const bysetpos = dayOfMonth < 29 ? null : -1;
  let bymonthday: number | number[] = dayOfMonth;

  switch (dayOfMonth) {
    case 29:
      bymonthday = [28, 29];
      break;
    case 30:
      bymonthday = [28, 29, 30];
      break;
    case 31:
      bymonthday = [28, 29, 30, 31];
      break;
    default:
      break;
  }

  return { bysetpos, bymonthday };
}

export const repeatModelFrom = (date: string) => (() => {
  const tzid = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dFn = DateF.fromDateWithTimezoneOffset(date);
  let weekPosition = -1; // last occurence of month

  const dtstart = dFn.startOfDay().toVanillaDate();

  if (weeks.includes(dFn.toUtcNumericalWeekOfMonth())) {
    weekPosition = dFn.toUtcNumericalWeekOfMonth();
  }

  const weekday = dFn.toWeekdayString();
  const { bysetpos, bymonthday } = getByMonthDay(date);

  // Make sure the Scheduler doesn't fire early
  const rruleTime = { byhour: [0], byminute: [1], bysecond: [0] };

  const adjustDtstart = (options: Partial<Options>) => {
    const initRRule = new RRule({ ...options });
    const endOfDueDate = dFn.endOfDay().minusTimezoneOffset();
    const endOfToday = DateF.endOfToday().minusTimezoneOffset();
    const nextDtstart = initRRule.after(
      endOfToday.isBefore(endOfDueDate)
        ? endOfDueDate.toVanillaDate()
        : endOfToday.toVanillaDate(),
    );

    return new RRule({ ...options, dtstart: nextDtstart }).toString();
  };

  return {
    daily: () => adjustDtstart({
      freq: RRule.DAILY,
      tzid,
      dtstart,
      ...rruleTime,
    }),
    weekly: () => adjustDtstart({
      freq: RRule.WEEKLY,
      tzid,
      dtstart,
      ...rruleTime,
    }),
    biWeekly: () => adjustDtstart({
      freq: RRule.WEEKLY,
      tzid,
      dtstart,
      interval: 2,
      ...rruleTime,
    }),
    everyWeekday: () => adjustDtstart({
      freq: RRule.WEEKLY,
      tzid,
      dtstart,
      byweekday: [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR],
      ...rruleTime,
    }),
    everySpecificDayOfMonth: () => adjustDtstart({
      freq: RRule.MONTHLY,
      tzid,
      dtstart,
      ...(bysetpos && { bysetpos }),
      bymonthday,
      ...rruleTime,
    }),
    everyNthWeekdayOfMonth: () => adjustDtstart({
      freq: RRule.MONTHLY,
      tzid,
      dtstart,
      byweekday: weekdayMap[weekday],
      bysetpos: [weekPosition],
      ...rruleTime,
    }),
  };
})();

export default repeatModelFrom;
